import React, { useState } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  Placement,
  useMergeRefs,
} from "@floating-ui/react";
import { buildClasses } from "../utils/buildClasses";

interface RyeTooltipProps {
  children: React.ReactNode;
  text: React.ReactNode;
  placement?: Placement;
  delay?: number;
}

export function RyeTooltip({
  children,
  text,
  placement = "bottom",
  delay = 500,
}: RyeTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackAxisSideDirection: "start",
      }),
      shift(),
    ],
  });

  const hover = useHover(context, {
    move: false,
    delay: { open: delay, close: 0 },
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  const child = React.isValidElement(children) ? (
    children
  ) : (
    <span>{children}</span>
  );

  // We use cloneElement to inject the ref and event handlers directly into the child
  // element. This avoids adding a wrapper div that could break layout (e.g. overflow: hidden).
  // Note: The child must accept a ref (use forwardRef for custom components) and spread props.
  const { ref: childRef } = child as any;
  const ref = useMergeRefs([refs.setReference, childRef]);

  return (
    <>
      {React.cloneElement(
        child as React.ReactElement<any>,
        getReferenceProps({
          ref,
          ...child.props,
        }),
      )}
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={buildClasses(
              "z-[2147483647]",
              "px-2",
              "py-1",
              "text-xs",
              "text-surface-50",
              "bg-on-surface-800",
              "rounded",
              "shadow-popover",
              "pointer-events-none",
              "opacity-90",
              "max-w-xs",
              "whitespace-normal",
            )}
          >
            {text}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
